import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { FiEdit, FiTrash } from 'react-icons/fi';
//import { useRef } from 'react';

const ClasseMatieresPage = () => {
  const { selectedYear } = useAcademicYear();
  const [matieres, setMatieres] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const [classeMatieres, setClasseMatieres] = useState([]);
  const [professeurs, setProfesseurs] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [profQualif, setProfQualif] = useState([]);
  const [, setMatieresLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    coef: '',
    ordre_bulletin: '',
    professeur_id: '',
    groupe_id: '',
    calculated: 'Y',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const fetchData = async () => {
    if (!selectedYear) return;
    const [matRes, ouvRes, cmRes, profRes, qualifRes, profQualifRes] = await Promise.all([
      api.get('/matieres'),
      api.get('/ouvertures-classes'),
      api.get('/classe-matieres'),
      api.get('/professeurs'),
      api.get('/qualifications'),
      api.get('/prof-qualif'),
    ]);
    setMatieres(matRes.data.data || []);
    setOuvertures((ouvRes.data.data || []).filter(o => o.annee_scolaire_id === selectedYear.id));
    setClasseMatieres(cmRes.data.data || []);
    setProfesseurs(profRes.data.data || []);
    setQualifications(qualifRes.data.data || []);
    setProfQualif(profQualifRes.data.data || []);
    
    setMatieresLoaded(true);
  };

  // Matières non encore liées à toutes les ouvertures de classe de l'année sélectionnée
  const getAvailableMatieres = () => {
    // Affiche toutes les matières (pour pouvoir les glisser dans n'importe quelle colonne)
    return matieres;
  };

  // Drag and drop handler
  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const matiereId = parseInt(result.draggableId.replace('matiere-', ''));
    const ouvertureId = result.destination.droppableId.replace('ouverture-', '');
    // Créer la liaison
    try {
      await api.post('/classe-matieres', {
        ouverture_classe_id: ouvertureId,
        matiere_id: matiereId,
        groupe_id: matiereId, // Par défaut, le groupe est la matière elle-même
        calculated: 'Y', // Par défaut, pris en compte dans la moyenne
      });
      fetchData();
    } catch (e) {
      Swal.fire('Erreur', 'Impossible de lier la matière à la classe.', 'error');
    }
  };

  // Ouvre le modal d'édition
  const handleEdit = (cm) => {
    setEditing(cm);
    setForm({
      coef: cm.coef || '',
      ordre_bulletin: cm.ordre_bulletin || '',
      professeur_id: cm.professeur_id || '',
      groupe_id: cm.groupe_id || cm.matiere_id || '',
      calculated: cm.calculated || 'Y',
    });
    setModalOpen(true);
  };

  // Supprime la liaison
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette liaison ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/classe-matieres/${id}`);
      fetchData();
      Swal.fire('Supprimé !', 'La liaison a été supprimée.', 'success');
    }
  };

  // Enregistre le modal
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api.put(`/classe-matieres/${editing.id}`, {
        ...editing,
        ...form,
        coef: form.coef,
        ordre_bulletin: form.ordre_bulletin,
        professeur_id: form.professeur_id || null,
        groupe_id: form.groupe_id || editing.matiere_id,
        calculated: form.calculated,
      });
      setModalOpen(false);
      setEditing(null);
      fetchData();
    }
  };

  return (
    <div>
      <h1 className="page-title mb-4 sm:mb-6">Affectation des matières aux classes ouvertes</h1>
      <div className="kanban-scroll h-[calc(100vh-140px)] sm:h-[calc(100vh-120px)]">
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Colonne matières disponibles sticky à gauche */}
          <Droppable droppableId="matieres-disponibles">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="kanban-column bg-gray-50 sticky left-0 z-30">
                <h2 className="font-bold mb-2 text-center sticky top-0 z-30 bg-gray-50">Matières disponibles</h2>
                {getAvailableMatieres().map((m, idx) => (
                  <Draggable key={m.id} draggableId={`matiere-${m.id}`} index={idx}>
                    {(prov) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        className="bg-white rounded shadow p-2 mb-2 cursor-move text-center"
                      >
                        {m.lib_matiere}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {/* Colonnes par ouverture de classe */}
          {(() => {
            return ouvertures.map((ocl, index) => {
              return (
                <Droppable droppableId={`ouverture-${ocl.ouverture_classe_id || ocl.id}`} key={ocl.ouverture_classe_id || ocl.id}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="kanban-column bg-blue-50">
                      <h2 className="font-bold mb-2 text-center sticky top-0 z-10 bg-blue-50">{ocl.classe?.classe || ocl.classe_id}</h2>
                  {(classeMatieres.filter(cm => {
                    const cmOuvertureId = cm.ouverture_classe_id;
                    const oclId = ocl.ouverture_classe_id || ocl.id;
                    return cmOuvertureId === oclId;
                  }).map((cm, idx) => {
                    const mat = matieres.find(m => m.id === cm.matiere_id);
                    return (
                      <div key={cm.id} className="bg-white rounded shadow p-2 mb-2 flex items-center justify-between">
                        <span>{mat ? mat.lib_matiere : cm.matiere_id}</span>
                        <div className="flex gap-1">
                          <button className="text-primary-600 hover:text-primary-900" onClick={() => handleEdit(cm)} title="Modifier"><FiEdit /></button>
                          <button className="text-red-600 hover:text-red-900" onClick={() => handleDelete(cm.id)} title="Supprimer"><FiTrash /></button>
                        </div>
                      </div>
                    );
                  }))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
              );
            });
          })()}
        </DragDropContext>
      </div>
      {/* Modal d'édition */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        contentLabel="Éditer la liaison"
        ariaHideApp={false}
        style={{
          overlay: { zIndex: 60, backgroundColor: 'rgba(0,0,0,0.3)' },
          content: { maxWidth: '400px', margin: 'auto', borderRadius: 12, padding: 0, height:'600px', zIndex: 61 }
        }}
      >
        <form onSubmit={handleModalSubmit} className="p-6">
          <h2 className="text-lg font-semibold mb-4">Modifier la liaison</h2>
          {editing && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Matière</label>
              <div className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700">
                {matieres.find(m => m.id === editing.matiere_id)?.lib_matiere || editing.matiere_id}
              </div>
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Coefficient</label>
            <input type="number" name="coef" value={form.coef} onChange={e => setForm({ ...form, coef: e.target.value })} className="w-full border rounded px-3 py-2" step="0.01" min="0" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Ordre bulletin</label>
            <input type="number" name="ordre_bulletin" value={form.ordre_bulletin} onChange={e => setForm({ ...form, ordre_bulletin: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Professeur</label>
            <select
              name="professeur_id"
              value={form.professeur_id}
              onChange={e => setForm({ ...form, professeur_id: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">-- Sélectionner --</option>
              {(() => {
                if (!editing) return null;
                // Trouver la matière sélectionnée
                const matiereId = editing.matiere_id;
                // Trouver les qualifications pour cette matière
                const qualifIds = qualifications.filter(q => q.matiere_id === matiereId).map(q => q.id);
                // Filtrer les professeurs qui ont une qualification dans cette matière via prof_qualif
                const profsQualifies = profQualif
                  .filter(pq => qualifIds.includes(pq.qualification_id))
                  .map(pq => pq.professeur_id);
                // Afficher les professeurs uniques
                return professeurs.filter(prof => profsQualifies.includes(prof.id)).map(prof => (
                  <option key={prof.id} value={prof.id}>{prof.nom} {prof.prenom}</option>
                ));
              })()}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Regroupement par matière</label>
            <select
              name="groupe_id"
              value={form.groupe_id}
              onChange={e => setForm({ ...form, groupe_id: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">-- Par défaut ({editing?.matiere_id && matieres.find(m => m.id === editing.matiere_id)?.lib_matiere}) --</option>
              {matieres.map(m => (
                <option key={m.id} value={m.id}>{m.lib_matiere}</option>
              ))}
            </select>
          </div>
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="calculated"
              checked={form.calculated === 'Y'}
              onChange={e => setForm({ ...form, calculated: e.target.checked ? 'Y' : 'N' })}
              className="mr-2"
            />
            <label htmlFor="calculated" className="text-sm font-medium">Pris en compte dans la moyenne</label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
            <button type="submit" className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">Enregistrer</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClasseMatieresPage;